import { Wallet } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { ClipboardList, Building2, Boxes, Users, Settings2, LogOut, Menu, ShieldCheck, Warehouse, ScanSearch, Wrench, History, Tags } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Órdenes de servicio", icon: ClipboardList, end: true, id: "nav-work-orders" },
  { to: "/admin/clientes", label: "Clientes y proyectos", icon: Building2, id: "nav-clients" },
  { to: "/admin/catalogo", label: "Catálogo de equipos", icon: Boxes, id: "nav-catalog" },
  { to: "/admin/catalogo-comercial", label: "Catálogo comercial", icon: Tags, perm: "settings.write", id: "nav-commercial-catalog" },
  { to: "/admin/levantamientos", label: "Levantamientos", icon: ScanSearch, perm: "surveys.read_all", id: "nav-surveys" },
  { to: "/admin/mantenimientos", label: "Mantenimientos", icon: Wrench, perm: "wo.read_all", id: "nav-maintenance" },
  { to: "/admin/finanzas", label: "Finanzas", icon: Wallet, perm: "finance.read", id: "nav-finance" },
  { to: "/admin/historial-equipos", label: "Historial de equipos", icon: History, perm: "wo.read_all", id: "nav-equipment-history" },
  { to: "/admin/inventario", label: "Inventario", icon: Warehouse, perm: "inventory.read", id: "nav-inventory" },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users, perm: "users.read", id: "nav-users" },
  { to: "/admin/configuracion", label: "Configuración", icon: Settings2, perm: "settings.write", id: "nav-settings" },
];

const SidebarBody = ({ onNavigate }) => {
  const { user, meta, can, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 px-4 py-5">
        <div className="rounded-lg bg-white px-3 py-2"><BrandLogo className="h-9 w-auto" testid="sidebar-logo" /></div>
        <div className="flex items-center gap-2 px-1">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="font-display text-base font-extrabold leading-none tracking-tight">TSSMX <span className="text-primary">OS</span></span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{meta?.tenant?.name}</span>
        </div>
      </div>
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3">
        {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={n.id}
            className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
              isActive ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role_label}</div>
        <Button variant="ghost" size="sm" className="mt-3 w-full justify-start px-2 text-muted-foreground" onClick={logout} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
        </Button>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-border bg-card/40 lg:block"><SidebarBody /></aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/90 px-4 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] backdrop-blur-xl lg:hidden">
        <span className="font-display text-lg font-extrabold">TSSMX <span className="text-primary">OS</span></span>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button></SheetTrigger>
          <SheetContent side="left" className="bottom-[env(safe-area-inset-bottom)] top-[env(safe-area-inset-top)] h-auto w-72 bg-background p-0"><SidebarBody onNavigate={() => setOpen(false)} /></SheetContent>
        </Sheet>
      </header>
      <main className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="fade-up mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      {eyebrow && <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">{eyebrow}</p>}
      <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
